import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Popover, Chip, Divider, Button, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {main_url} from  './basefile';
import { formatDate } from '../utils/dateFormat';

const mockScheduleData = {
  results: [
    {
      id: 1,
      execution: 1,
      actual_date: "2025-08-10T00:00:00Z",
      status: "completed",
      schedule_type: "dynamic",
      created_at: "2025-07-03T07:14:00.517Z",
      updated_at: "2025-07-03T07:14:00.517Z",
    },
    {
      id: 2,
      execution: 1,
      actual_date: "2025-08-15T00:00:00Z",
      status: "completed",
      schedule_type: "dynamic",
      created_at: "2025-07-04T08:14:00.517Z",
      updated_at: "2025-07-04T08:14:00.517Z",
    },
    {
      id: 3,
      execution: 1,
      actual_date: "2025-08-20T00:00:00Z",
      status: "planned",
      schedule_type: "dynamic",
      created_at: "2025-07-05T10:14:00.517Z",
      updated_at: "2025-07-05T10:14:00.517Z",
    },
    {
      id: 4,
      execution: 1,
      actual_date: null,
      status: "planned",
      schedule_type: "dynamic",
      created_at: "2025-07-06T10:14:00.517Z",
      updated_at: "2025-07-06T10:14:00.517Z",
    },
  ]
};


const DAYS_WINDOW = 15;

const ScheduleCalendar = ({ execution_pk }) => {
  const [dateInfo, setDateInfo] = useState({});
  const [windowStart, setWindowStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - DAYS_WINDOW);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [headerLabel, setHeaderLabel] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSchedules, setSelectedSchedules] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  const daysInWindow = useMemo(() => {
    const days = [];
    const current = new Date(windowStart);
    const totalDays = 2 * DAYS_WINDOW; // 30-day window
    for (let i = 0; i < totalDays; i += 1) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  }, [windowStart]);

  const calendarCells = useMemo(() => {
    const leadingEmpty = windowStart.getDay(); // 0=Sun .. 6=Sat
    const cells = [];
    for (let i = 0; i < leadingEmpty; i += 1) cells.push(null);
    (daysInWindow || []).forEach((d) => cells.push(d));
    // Pad to full weeks (usually 35 cells for 5 rows, sometimes 42 for 6).
    while (cells.length % 7 !== 0) cells.push(null);
    // Keep UI consistent with previous 5-row layout when possible.
    if (cells.length < 35) {
      while (cells.length < 35) cells.push(null);
    }
    return cells;
  }, [daysInWindow, windowStart]);

  const makeDateKey = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const windowEnd = useMemo(() => {
    const end = new Date(windowStart);
    end.setDate(end.getDate() + (2 * DAYS_WINDOW) - 1);
    end.setHours(23, 59, 59, 999);
    return end;
  }, [windowStart]);

  const fetchScheduleDates = async (startDate) => {
    const start_date_obj = new Date(startDate);
    start_date_obj.setHours(0, 0, 0, 0);
    const end_date_obj = new Date(start_date_obj);
    end_date_obj.setDate(end_date_obj.getDate() + (2 * DAYS_WINDOW) - 1);

    const start_date = start_date_obj.toISOString().split("T")[0];
    const end_date = end_date_obj.toISOString().split("T")[0];

    const startLabel = start_date_obj.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
    const endLabel = end_date_obj.toLocaleString("default", {
      month: "long",
      year: "numeric",
    });
    setHeaderLabel(startLabel === endLabel ? startLabel : `${startLabel} - ${endLabel}`);

    const token = localStorage.getItem('authToken');
    try {
      const res = await fetch(`${main_url}/api/v1/dashboard/list_schedules/?start_date=${start_date}&end_date=${end_date}&page=1&limit=100`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json();
      const info = {};
      (data.results || []).forEach((item) => {
        const status = String(item.status || "").toLowerCase();

        const upsertDay = (key, kind) => {
          if (!key) return;
          if (!info[key]) {
            info[key] = {
              hasCompleted: false,
              hasPlanned: false,
              hasOptimal: false,
              schedules: [],
            };
          }

          if (kind === "actual") {
            // Only actual/scheduled dates contribute to planned/completed coloring.
            if (status === "completed") {
              info[key].hasCompleted = true;
            } else {
              info[key].hasPlanned = true;
            }
          } else if (kind === "optimal") {
            info[key].hasOptimal = true;
          }

          info[key].schedules.push({ ...item, __dateKind: kind });
        };

        // Use the date portion as sent by backend (CST context), avoid timezone shifts in JS Date.
        const actualKey = item.actual_date ? String(item.actual_date).slice(0, 10) : "";
        const optimalKey = item.optimal_date ? String(item.optimal_date).slice(0, 10) : "";

        // Scheduled / actual date marker (completed or planned)
        if (actualKey) upsertDay(actualKey, "actual");

        // Optimal date marker: if patient hasn't scheduled yet but optimal exists, highlight that day.
        // We treat "hasn't scheduled" as missing actual_date.
        if (!actualKey && optimalKey) upsertDay(optimalKey, "optimal");
      });
      setDateInfo(info);
    } catch (err) {
      console.error('Error fetching schedule data:', err);
    }
  };

  useEffect(() => {
    fetchScheduleDates(windowStart);
  }, [windowStart]);

  const goPrevious = () => {
    const next = new Date(windowStart);
    next.setDate(next.getDate() - DAYS_WINDOW);
    setWindowStart(next);
  };

  const goNext = () => {
    const next = new Date(windowStart);
    next.setDate(next.getDate() + DAYS_WINDOW);
    setWindowStart(next);
  };

  const handleDayClick = (value, event) => {
    const key = makeDateKey(value);
    const info = dateInfo[key];
    if (!info || !info.schedules || info.schedules.length === 0) {
      return;
    }
    setSelectedDate(key);
    setSelectedSchedules(info.schedules);
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedSchedules([]);
    setSelectedDate(null);
  };

  return (
    <>
     <Box sx={{ width: '100%', p: 0, pb: 3 }}>
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#e3f2fd',
        borderRadius: '10px',
        padding: '16px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Button
          size="small"
          startIcon={<ChevronLeftIcon />}
          onClick={goPrevious}
          variant="outlined"
        >
          Previous 15 days
        </Button>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: '#0d47a1', textAlign: 'center' }}
        >
          {headerLabel}
        </Typography>
        <Button
          size="small"
          endIcon={<ChevronRightIcon />}
          onClick={goNext}
          variant="outlined"
        >
          Next 15 days
        </Button>
      </Box>
      {/* Custom 30-day window calendar grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 1,
          mt: 2,
        }}
      >
        {/* Weekday labels */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((label) => (
          <Box
            key={label}
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              color: '#0d47a1',
              pb: 0.5,
            }}
          >
            {label}
          </Box>
        ))}

        {/* Date cells – 30-day window aligned by weekday */}
        {calendarCells.map((date, index) => {
          if (!date) {
            return (
              <Box
                key={`empty-${index}`}
                sx={{ minHeight: 48 }}
              />
            );
          }

          const key = makeDateKey(date);
          const info = dateInfo[key];
          const hasCompleted = info?.hasCompleted;
          const hasPlanned = info?.hasPlanned;
          const hasOptimal = info?.hasOptimal;
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          const hasHighlight = Boolean(hasCompleted || hasPlanned || hasOptimal);

          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const isToday = date.getTime() === today.getTime();

          const baseCellShadow =
            hasCompleted
              ? "0 5px 12px rgba(46, 125, 50, 0.45)"
              : hasPlanned
                ? "0 5px 12px rgba(61, 121, 212, 0.6)"
                : hasOptimal
                  ? "0 5px 12px rgba(251, 140, 0, 0.35)"
                  : "none";

          const todayRingShadow = "0 0 0 3px rgba(13, 71, 161, 0.45)";
          const showTodayRing = Boolean(isToday && baseCellShadow === "none");

          const bgGradient =
            hasCompleted
              ? "linear-gradient(135deg, #2e7d32, #66bb6a)"
              : hasPlanned
                ? "linear-gradient(135deg, #2c5fb6, #4c8ae0)"
                : hasOptimal
                  ? "linear-gradient(135deg, #fdd835, #fff59d)"
                  : isWeekend
                    ? "#ededed"
                    : "#e3f2fd";

          const color =
            hasCompleted || hasPlanned
              ? "#ffffff"
              : hasOptimal
                ? "#3b2f00"
                : isWeekend
                  ? "#b71c1c"
                  : "#0d47a1";

          const tooltipLabel = `${String(date.getDate()).padStart(2, '0')}-${date
            .toLocaleString('en-US', { month: 'short' })
            .toUpperCase()}-${date.getFullYear()}`;

          return (
            <Tooltip key={key} title={tooltipLabel} arrow>
              <Box
                onClick={(event) => handleDayClick(date, event)}
                sx={{
                  cursor: info?.schedules?.length ? 'pointer' : 'default',
                  minHeight: 52,
                  borderRadius: '6px',
                  background: bgGradient,
                  color,
                  boxShadow:
                    baseCellShadow === "none"
                      ? showTodayRing
                        ? todayRingShadow
                        : "none"
                      : baseCellShadow,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: "relative",
                  fontWeight: isToday ? 800 : 500,
                  border: hasOptimal && !hasCompleted && !hasPlanned ? "1px solid #fbc02d" : "1px solid transparent",
                  '&:hover': {
                    transform: hasCompleted || hasPlanned || hasOptimal ? 'scale(1.05)' : 'none',
                    boxShadow:
                      baseCellShadow === "none"
                        ? showTodayRing
                          ? todayRingShadow
                          : "none"
                        : baseCellShadow,
                  },
                }}
              >
                {date.getDate()}

                {/* Status badge (matches cell highlight theme) */}
                {hasHighlight ? (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: hasCompleted
                        ? "#2e7d32"
                        : hasPlanned
                          ? "#2c5fb6"
                          : "#fdd835",
                      border: hasOptimal && !hasPlanned && !hasCompleted ? "1px solid #f9a825" : "1px solid rgba(255,255,255,0.7)",
                      boxShadow: hasOptimal && !hasPlanned && !hasCompleted ? "0 0 0 2px rgba(251, 140, 0, 0.22)" : "none",
                    }}
                  />
                ) : null}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  </Box>    
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={handlePopoverClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
  >
    <Box sx={{ p: 2, minWidth: 260, maxWidth: 360, bgcolor: '#ffffff' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        Schedules on {formatDate(selectedDate)}
      </Typography>
      <Divider sx={{ mb: 1 }} />
      {selectedSchedules.map((item, index) => (
        <Box
          key={item.id ?? index}
          sx={{ mb: 1.5, p: 1, borderRadius: 1, bgcolor: '#f5f5f5' }}
        >
          <Typography variant="body2" component="div" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {item.patient?.id ? (
              <RouterLink
                to={`/subject-management/edit/${item.patient.id}`}
                onClick={handlePopoverClose}
                style={{
                  color: 'inherit',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                }}
              >
                {item.patient?.name || 'Subject'}
              </RouterLink>
            ) : (
              item.patient?.name || 'Subject'
            )}
            {item.project?.id && item.project?.name ? (
              <>
                {' '}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  –
                </Box>{' '}
                <RouterLink
                  to={`/project-details/${item.project.id}`}
                  onClick={handlePopoverClose}
                  style={{
                    color: 'inherit',
                    fontWeight: 600,
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                  }}
                >
                  {item.project.name}
                </RouterLink>
              </>
            ) : null}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {item.__dateKind === "optimal" ? "Optimal Date" : "Scheduled Date"}:{" "}
            {formatDate(item.__dateKind === "optimal" ? item.optimal_date : item.actual_date)}
          </Typography>
          <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={item.status || 'Status'}
              color={
                String(item.__dateKind || "") === "optimal"
                  ? "warning"
                  : item.status === 'completed'
                    ? 'success'
                    : 'primary'
              }
              variant="filled"
              sx={
                String(item.__dateKind || "") === "optimal"
                  ? {
                      bgcolor: "#fdd835",
                      color: "#3b2f00",
                      fontWeight: 700,
                      "& .MuiChip-label": { px: 1 },
                    }
                  : undefined
              }
            />
            {item.schedule_type && (
              <Chip size="small" label={item.schedule_type} variant="outlined" />
            )}
          </Box>
        </Box>
      ))}
      {selectedSchedules.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No schedules for this day.
        </Typography>
      )}
    </Box>
  </Popover>
  </>
  );
};

export default ScheduleCalendar;
